import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Box, Button, Fade, Hidden, MenuItem, Typography } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import {
  DocumentGenerationTemplate_FieldsFragment,
  DocumentOrderBy,
  EntityOrderDirection,
  Estate_UpdateEstateInput,
  PortalPermissions,
  useDocument_GetFilesQuery,
  useEstate_GetEstateByIdQuery,
  useEstate_UpdateEstateMutation,
  useEstateShareMembership_GetEstateShareMembershipQuery,
} from 'queries';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import DocumentUploadButton from 'pages/common/document/document-upload-button';
import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import { BulkUploadModal } from './components/bulk-upload/bulk-upload';
import EstateDocumentsTable, { DocumentTab } from './components/estate-documents-table';
import InstructionsModal from './components/instructions-modal';

const useStyles = makeStyles((theme) => ({
  parentContainer: {
    color: '#1C2541',
  },
  pageTitle: {
    marginBottom: '24px',
  },
  estateIconContainer: {
    width: 48,
    height: 48,
    color: '#fff',
    fontSize: 32,
    fontWeight: 700,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    marginRight: 16,
  },
  gridContainer: {
    '.MuiDataGrid-columnHeadersInner': {
      backgroundColor: '#F9F9FB',
    },
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: '24px',
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '40px',
    marginBottom: '40px',
  },
  estateDashboardButton: {
    marginLeft: '16px',
    backgroundColor: '#F9F9FB',
    color: '#1C2541',
    borderRadius: '8px',
    fontSize: '14px',
    boxShadow: 'none',
  },
  estateDashboardButtonIcon: {
    marginRight: '8px',
  },
  leftHeaderContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  textContainer: { flexGrow: 1, paddingLeft: '80px', paddingRight: '80px' },
  fullWidth: { width: '100%' },
  tabsContainer: { borderBottom: '1px solid #ECEEF1', marginBottom: '8px' },
  documentsForSignatureTab: {
    display: 'flex',
    alignItems: 'center',
  },
  dotNotification: {
    width: theme.spacing(1),
    height: theme.spacing(1),
    backgroundColor: theme.palette.success.main,
    borderRadius: '50%',
    marginLeft: theme.spacing(1),
  },
  tabBox: {
    display: 'flex',
    alignItems: 'center',
  },
  bulkUploadButton: {
    backgroundColor: '#F9F9FB',
    color: '#1C2541',
    borderRadius: '8px',
    boxShadow: 'none',
    fontSize: '14px',
    px: '16px',
    margin: '0 16px',
  },
  buttonIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '8px',
  },
}));

const searchTextFieldStyles = {
  borderRadius: '24px',
  height: '36px',
  backgroundColor: '#F9F9FB',
};

const filterTextFieldStyles = {
  height: '36px',
  width: '240px',
  backgroundColor: '#F9F9FB',
};

const sortOptions = [
  { value: DocumentOrderBy.UpdatedAt, label: 'Newest first' },
  { value: DocumentOrderBy.Name, label: 'Name ascending' },
];

interface Props {
  hideNav?: boolean;
  folderId: string;
}

const EstateVault = ({ hideNav = false, folderId }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId, estateId, folder: folderParam } = useParams<Record<string, string>>();
  const folder = folderParam || folderId;

  const hasPortalPermissionClient = useHasPortalPermission(PortalPermissions.Client);

  const [searchText, setSearchText] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [orderBy, setOrderBy] = useState<DocumentOrderBy>(DocumentOrderBy.UpdatedAt);
  const [orderDirection, setOrderDirection] = useState<EntityOrderDirection>(
    EntityOrderDirection.Descending,
  );
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  const [
    documentGenerationTemplateToShowInstructions,
    setDocumentGenerationTemplateToShowInstructions,
  ] = useState<DocumentGenerationTemplate_FieldsFragment | null>(null);

  const { data: estateData, loading: estateLoading } = useEstate_GetEstateByIdQuery({
    variables: { id: estateId },
    onError: (e) =>
      showAlert({
        message: 'Error getting estate by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const {
    data: estateDocumentsData,
    refetch: refetchEstateDocuments,
    loading: estateDocumentsLoading,
  } = useDocument_GetFilesQuery({
    variables: {
      input: { portalId, parentId: folder, orderBy, orderDirection },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting files: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: estateShareMembershipData, loading: estateShareMembershipLoading } =
    useEstateShareMembership_GetEstateShareMembershipQuery({
      variables: { input: { estateId, portalId } },
      onError: (e) => {
        showAlert({
          message: 'Error getting estate share membership: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [updateEstate] = useEstate_UpdateEstateMutation({
    onError: (e) => {
      showAlert({
        message: 'Error updating estate: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleUploadCompleted = async () => {
    await refetchEstateDocuments();
  };

  const onOrderBy = (orderField: DocumentOrderBy) => {
    setOrderBy(orderField);
    setOrderDirection(
      orderDirection === EntityOrderDirection.Ascending
        ? EntityOrderDirection.Descending
        : EntityOrderDirection.Ascending,
    );
  };

  let estate = estateData?.data;

  const markDocumentTabSeen = (tab: DocumentTab) => {
    const updateEstateInput: Estate_UpdateEstateInput = {
      id: estate!.id,
      portalId,
    };

    switch (tab) {
      case DocumentTab.SearchResults:
        //estate = { ...estate, vaultHasNewSearchResultDocuments: false }; // update UI immediately
        updateEstateInput.vaultHasNewSearchResultDocuments = false;
        break;
      case DocumentTab.GeneratedDocuments:
        //estate = { ...estate, vaultHasNewGeneratedDocuments: false }; // update UI immediately
        updateEstateInput.vaultHasNewGeneratedDocuments = false;
        break;
      case DocumentTab.DocumentsForSignature:
        //estate = { ...estate, vaultHasNewDocumentsForSignature: false }; // update UI immediately
        updateEstateInput.vaultHasNewDocumentsForSignature = false;
        break;

      case DocumentTab.Uploads:
      default:
        break;
    }

    updateEstate({
      variables: {
        input: updateEstateInput,
      },
    });
  };

  useEffect(() => {
    if (estate) {
      switch (currentTab) {
        case 1:
          if (estate.vaultHasNewSearchResultDocuments) {
            markDocumentTabSeen(DocumentTab.SearchResults);
          }
          break;
        case 2:
          if (estate.vaultHasNewGeneratedDocuments) {
            markDocumentTabSeen(DocumentTab.GeneratedDocuments);
          }
          break;
        case 3:
          if (estate.vaultHasNewDocumentsForSignature) {
            markDocumentTabSeen(DocumentTab.DocumentsForSignature);
          }
          break;

        case 0:
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, estate]);

  const isLoading =
    estateLoading ||
    !estateData ||
    estateDocumentsLoading ||
    !estateDocumentsData ||
    estateShareMembershipLoading ||
    !estateShareMembershipData;

  if (isLoading) {
    return <Spinner />;
  }

  if (hasPortalPermissionClient && !estateShareMembershipData.data?.isActive) {
    // If shared client user, send to home page if they don't have access to this estate
    history.replace(`/home/${portalId}/home`);
    return <Spinner />;
  }

  const allDocuments = estateDocumentsData.data || [];
  const filteredDocuments = searchText
    ? allDocuments.filter((document) =>
        document.name.toLowerCase().includes(searchText.toLowerCase()),
      )
    : allDocuments;

  const uploadedDocuments = filteredDocuments.filter(
    (document) => document.isFileUploadedAndSignedIfNeeded,
  );
  const generatedDocuments = filteredDocuments.filter(
    (document) => document.isFileGeneratedAndSignedIfNeeded,
  );
  const searchResultDocuments = filteredDocuments.filter(
    (document) => document.isFileSearchResultAndSignedIfNeeded,
  );
  const documentsForSignature = filteredDocuments.filter(
    (document) => document.isFilePendingSignature,
  );

  const content = (
    <Fade in={true}>
      <div className={classes.parentContainer}>
        {documentGenerationTemplateToShowInstructions && (
          <InstructionsModal
            showModal={Boolean(documentGenerationTemplateToShowInstructions)}
            setShowModal={() => {
              setDocumentGenerationTemplateToShowInstructions(null);
            }}
            documentGenerationTemplate={documentGenerationTemplateToShowInstructions}
          />
        )}
        {!hideNav && <Typography className={classes.title}>{estate!.estateName}</Typography>}
        <Box className={classes.actionButtons}>
          <div className={classes.leftHeaderContainer}>
            <DocumentUploadButton folderId={folder} onUploadComplete={handleUploadCompleted} />
            <Button
              component="span"
              className={classes.bulkUploadButton}
              color="primary"
              variant="contained"
              size="small"
              onClick={() => setShowBulkUploadModal(true)}
            >
              <div className={classes.buttonIcon}>
                <DriveFolderUploadOutlinedIcon />
              </div>
              <Hidden mdDown>
                <Typography>Bulk Upload</Typography>
              </Hidden>
            </Button>
            <Button
              component="span"
              variant="contained"
              size="small"
              className={classes.estateDashboardButton}
              onClick={() =>
                history.push(
                  `/home/${portalId}/estate/${estateId}/estate-dashboard?tab=${EstateDashboardTab.ESTATE_CHECKLIST}`,
                )
              }
            >
              <WorkOutlineOutlinedIcon className={classes.estateDashboardButtonIcon} />
              Estate Dashboard
            </Button>
          </div>
          <Box className={classes.textContainer}>
            <TextField
              fullWidth
              placeholder="Search Vault"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                style: searchTextFieldStyles,
              }}
              variant="outlined"
            />
          </Box>
          <div>
            <TextField
              select
              defaultValue={DocumentOrderBy.UpdatedAt}
              onChange={(e) => onOrderBy(e.target.value as DocumentOrderBy)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterListIcon fontSize="small" />
                  </InputAdornment>
                ),
                style: filterTextFieldStyles,
              }}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </Box>

        <Box className={classes.fullWidth}>
          <Box className={classes.tabsContainer}>
            <Tabs
              value={currentTab}
              onChange={(_: React.SyntheticEvent, newValue: number) => {
                setCurrentTab(newValue);
              }}
              textColor="inherit"
            >
              <Tab label="Uploads" />
              <Tab
                label={
                  <Box className={classes.tabBox}>
                    <span>Search Correspondence</span>
                    {estate!.vaultHasNewSearchResultDocuments && (
                      <Box className={classes.dotNotification} />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box className={classes.tabBox}>
                    <span>Docs for Review</span>
                    {estate!.vaultHasNewGeneratedDocuments && (
                      <Box className={classes.dotNotification} />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <div className={classes.documentsForSignatureTab}>
                    Signatures Needed{' '}
                    {estate!.vaultHasNewDocumentsForSignature && (
                      <Box className={classes.dotNotification} />
                    )}
                  </div>
                }
              />
            </Tabs>
          </Box>
        </Box>
        {currentTab === 0 && (
          <EstateDocumentsTable
            documents={uploadedDocuments}
            onRefreshDocuments={refetchEstateDocuments}
            tab={DocumentTab.Uploads}
          />
        )}
        {currentTab === 1 && (
          <EstateDocumentsTable
            documents={searchResultDocuments}
            onRefreshDocuments={refetchEstateDocuments}
            tab={DocumentTab.SearchResults}
          />
        )}
        {currentTab === 2 && (
          <EstateDocumentsTable
            documents={generatedDocuments}
            onRefreshDocuments={refetchEstateDocuments}
            tab={DocumentTab.GeneratedDocuments}
            setDocumentGenerationTemplateToShowInstructions={
              setDocumentGenerationTemplateToShowInstructions
            }
          />
        )}
        {currentTab === 3 && (
          <EstateDocumentsTable
            documents={documentsForSignature}
            onRefreshDocuments={refetchEstateDocuments}
            tab={DocumentTab.DocumentsForSignature}
          />
        )}
        {showBulkUploadModal && (
          <BulkUploadModal
            onClose={() => setShowBulkUploadModal(false)}
            setShowModal={setShowBulkUploadModal}
            showModal={showBulkUploadModal}
            portalId={portalId}
            parentId={folder}
          />
        )}
      </div>
    </Fade>
  );

  return hideNav ? content : <HomeLayout>{content}</HomeLayout>;
};

export default EstateVault;
